// 推薦收益提領 — 提領紀錄分頁（2026-06-07）
// 餘額卡 + 申請提領鈕（複用 WithdrawalDialog）+ 紀錄表（狀態 chip + 取消）
import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { Box, Card, Chip, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import dayjs from "dayjs";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import WithdrawalDialog from "./WithdrawalDialog";
import {
  getWithdrawalEligibility,
  getMyReferralWithdrawals,
  cancelReferralWithdrawal,
} from "../../../../api/userApi";
import { LanguageContext } from "../../../../context/LanguageContext";
import { COLORS } from "theme/tokens";

const ORANGE = COLORS.main;
const PAGE_SIZE = 10;

const fmtDateTime = (v) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-");

const STATUS_META = {
  PENDING: { key: "WdStatusPENDING", fb: "收益審核中", color: "#b45309", bg: "#fef3c7" },
  PROCESSING: { key: "WdStatusPROCESSING", fb: "收益發放中", color: "#1d4ed8", bg: "#dbeafe" },
  COMPLETED: { key: "WdStatusCOMPLETED", fb: "收益完成發放", color: "#15803d", bg: "#dcfce7" },
  REJECTED: { key: "WdStatusREJECTED", fb: "已拒絕", color: "#b91c1c", bg: "#fee2e2" },
  CANCELED: { key: "WdStatusCANCELED", fb: "已取消", color: COLORS.grayDark, bg: COLORS.grayLightest },
};

export default function WithdrawalHistory() {
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("ReferralTabs", "referral/ReferralTabs/ReferralTabs_lang");
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps
  const L = useMemo(
    () => (componentLanguageData.ReferralTabs || { Main: {} }).Main,
    [componentLanguageData]
  );
  const t = (k, fb) => L[k] || fb;

  const [eligibility, setEligibility] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchEligibility = useCallback(async () => {
    try {
      setEligibility(await getWithdrawalEligibility());
    } catch (e) {
      console.error("取得提領資格失敗:", e);
    }
  }, []);

  const fetchList = useCallback(async () => {
    try {
      const res = await getMyReferralWithdrawals({ page: page + 1, pageSize: PAGE_SIZE });
      setRows(res?.records || []);
      setTotal(res?.total || 0);
    } catch (e) {
      console.error("取得提領紀錄失敗:", e);
    }
  }, [page]);

  useEffect(() => {
    fetchEligibility();
  }, [fetchEligibility]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleCancel = async (id) => {
    if (!window.confirm(t("WdConfirmCancel", "確定要取消這筆提領申請嗎？"))) return;
    try {
      await cancelReferralWithdrawal(id);
      fetchEligibility();
      fetchList();
    } catch (e) {
      window.alert(e?.response?.data?.message || t("WdCancelFailed", "取消失敗"));
    }
  };

  const handleSuccess = () => {
    setDialogOpen(false);
    window.alert(t("WdSuccess", "提領申請已送出，審核中"));
    setPage(0);
    fetchEligibility();
    fetchList();
  };

  const available = Number(eligibility?.availableUsd || 0);
  const minAmount = Number(eligibility?.minAmount || 0);
  const canWithdraw = !!eligibility?.canWithdraw;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const GRID = "20% 16% 30% 18% 16%";

  return (
    <Box>
      {/* 餘額卡 + 申請鈕 */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          background: "#fff7f1",
          border: "1px solid #ffe2cf",
        }}
      >
        <Box>
          <MDTypography sx={{ fontSize: 14, color: COLORS.grayDark }}>
            {t("WdBalanceTitle", "可提領餘額")}
          </MDTypography>
          <MDTypography sx={{ fontSize: 32, fontWeight: 700, color: ORANGE, lineHeight: 1.2 }}>
            ${available.toFixed(2)}
          </MDTypography>
          {minAmount > 0 && (
            <MDTypography sx={{ fontSize: 12, color: COLORS.grayMedian }}>
              {t("WdMinHint", "最低提領金額為 {{min}} USD").replace("{{min}}", minAmount)}
            </MDTypography>
          )}
          {!canWithdraw && available < minAmount && (
            <MDTypography sx={{ fontSize: 12, color: "#e8833a", fontWeight: 600 }}>
              {t("WdShortfall", "目前可提領 {{avail}} USD，尚需 {{need}} USD 才達最低門檻")
                .replace("{{avail}}", available.toFixed(2))
                .replace("{{need}}", (minAmount - available).toFixed(2))}
            </MDTypography>
          )}
        </Box>
        <MDButton
          variant="gradient"
          color="warning"
          disabled={!canWithdraw}
          onClick={() => setDialogOpen(true)}
        >
          {t("WdApply", "申請提領")}
        </MDButton>
      </Box>

      {/* 紀錄表 */}
      <Card sx={{ borderRadius: 2, border: "1px solid #eef0f3", boxShadow: "none" }}>
        {/* header */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: GRID,
            px: 3,
            py: 1.5,
            backgroundColor: COLORS.grayLightest,
            borderBottom: "1px solid #f5f5f5",
          }}
        >
          {[
            t("WdColTime", "申請時間"),
            t("WdColAmount", "金額 (USD)"),
            t("WdColAccount", "收款帳號"),
            t("WdColStatus", "狀態"),
            t("WdColAction", "操作"),
          ].map((c, i) => (
            <Box key={i} sx={{ fontSize: 13, fontWeight: 600, color: COLORS.grayDark }}>
              {c}
            </Box>
          ))}
        </Box>

        {/* body */}
        {rows.length === 0 ? (
          <Box sx={{ py: 5, textAlign: "center", color: COLORS.grayDark, fontSize: 13 }}>
            {t("WdEmpty", "目前尚無提領紀錄")}
          </Box>
        ) : (
          rows.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.PENDING;
            return (
              <Box
                key={r.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: GRID,
                  px: 3,
                  py: 2,
                  borderBottom: "1px solid #f5f5f5",
                  alignItems: "center",
                  "&:hover": { backgroundColor: COLORS.grayLightest },
                }}
              >
                <Box sx={{ fontSize: 13, color: COLORS.black }}>{fmtDateTime(r.createdAt)}</Box>
                <Box sx={{ fontSize: 13, color: COLORS.black, fontWeight: 600 }}>
                  ${Number(r.amount).toFixed(2)}
                </Box>
                <Box sx={{ fontSize: 13, color: COLORS.black, wordBreak: "break-all" }}>
                  {r.payoutAccount || "-"}
                </Box>
                <Box>
                  <Chip
                    label={t(meta.key, meta.fb)}
                    size="small"
                    sx={{ color: meta.color, backgroundColor: meta.bg, fontWeight: 600, fontSize: 12 }}
                  />
                  {r.status === "REJECTED" && r.rejectReason && (
                    <MDTypography sx={{ fontSize: 11, color: "#b91c1c", mt: 0.5 }}>
                      {t("WdRejectReason", "拒絕原因")}: {r.rejectReason}
                    </MDTypography>
                  )}
                </Box>
                <Box>
                  {r.status === "PENDING" ? (
                    <MDButton
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleCancel(r.id)}
                      sx={{ fontSize: 12, textTransform: "none" }}
                    >
                      {t("WdCancelApply", "取消申請")}
                    </MDButton>
                  ) : (
                    <span style={{ color: COLORS.grayMedian }}>—</span>
                  )}
                </Box>
              </Box>
            );
          })
        )}

        {/* footer pager */}
        {total > PAGE_SIZE && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
              px: 3,
              py: 2,
              borderTop: "1px solid #eef0f3",
            }}
          >
            <IconButton
              size="small"
              disabled={page <= 0}
              onClick={() => setPage((p) => p - 1)}
              sx={{ width: 32, height: 32, border: "1px solid #e5e7eb", borderRadius: "8px" }}
            >
              <ArrowBackIcon sx={{ fontSize: 16, color: COLORS.grayDark }} />
            </IconButton>
            <Box sx={{ minWidth: 32, height: 32, px: 1.5, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: 13, fontWeight: 600 }}>
              {page + 1}
            </Box>
            <IconButton
              size="small"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              sx={{ width: 32, height: 32, border: "1px solid #e5e7eb", borderRadius: "8px" }}
            >
              <ArrowForwardIcon sx={{ fontSize: 16, color: COLORS.grayDark }} />
            </IconButton>
          </Box>
        )}
      </Card>

      <WithdrawalDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={handleSuccess}
        eligibility={eligibility}
        L={L}
      />
    </Box>
  );
}
